/**
 * useEncryptedStorage — Transparent AES-256-GCM encryption for on-device health data.
 *
 * Wraps Capacitor Preferences (with localStorage fallback) so every read/write
 * passes through the deviceEncryption layer, keyed to the user's auth session.
 *
 * If no session exists (logged-out state), data is stored as plaintext with a
 * warning — callers should avoid writing sensitive data without a session.
 */

import { useCallback, useRef, useEffect } from 'react';
import { Preferences } from '@capacitor/preferences';
import { supabase } from '@/integrations/supabase/client';
import { encryptHealthData, decryptHealthData } from '@/lib/deviceEncryption';

// Cache session token so we don't hit auth on every read/write
let cachedSessionToken: string | null = null;

async function getSessionToken(): Promise<string | null> {
  if (cachedSessionToken) return cachedSessionToken;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    cachedSessionToken = session?.access_token ?? null;
    return cachedSessionToken;
  } catch {
    return null;
  }
}

// Listen for auth changes to keep token fresh
supabase.auth.onAuthStateChange((_event, session) => {
  cachedSessionToken = session?.access_token ?? null;
});

/**
 * Set an encrypted value in Preferences (localStorage fallback).
 */
export async function setEncrypted(key: string, data: object): Promise<void> {
  const token = await getSessionToken();

  let payload: string;
  if (token) {
    payload = await encryptHealthData(data, token);
  } else {
    console.warn(`[EncryptedStorage] No session — storing ${key} as plaintext`);
    payload = JSON.stringify({ _unencrypted: true, data });
  }

  try {
    await Preferences.set({ key, value: payload });
  } catch {
    localStorage.setItem(key, payload);
  }
}

/**
 * Get and decrypt a value from Preferences (localStorage fallback).
 * Returns null if the key doesn't exist.
 */
export async function getEncrypted<T = object>(key: string): Promise<T | null> {
  let raw: string | null = null;

  try {
    const res = await Preferences.get({ key });
    raw = res.value;
  } catch {
    raw = localStorage.getItem(key);
  }

  if (!raw) return null;

  const token = await getSessionToken();

  // Try decrypting
  if (token) {
    try {
      return (await decryptHealthData(raw, token)) as T;
    } catch {
      // Might be legacy plaintext — try JSON parse
      try {
        const parsed = JSON.parse(raw);
        if (parsed._unencrypted) return parsed.data as T;
        // Legacy plain JSON (pre-encryption migration)
        return parsed as T;
      } catch {
        console.error(`[EncryptedStorage] Failed to read ${key}`);
        return null;
      }
    }
  }

  // No session — try plain JSON
  try {
    const parsed = JSON.parse(raw);
    if (parsed._unencrypted) return parsed.data as T;
    return parsed as T;
  } catch {
    return null;
  }
}

/**
 * Remove a key from storage.
 */
export async function removeEncrypted(key: string): Promise<void> {
  try {
    await Preferences.remove({ key });
  } catch {
    localStorage.removeItem(key);
  }
}

/**
 * React hook that exposes typed get/set/remove for encrypted on-device storage.
 */
export function useEncryptedStorage() {
  return {
    setEncrypted,
    getEncrypted,
    removeEncrypted,
  };
}

export default useEncryptedStorage;
